import { useEffect, useRef, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import AddButton from "@/components/Core/AddButton";
import AppAlertDialog from "@/components/Core/AppAlertDialog/AppAlertDialog";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import CheckboxNew from "@/components/Core/Checkbox/CheckboxNew";
import CertificationChip from "@/components/Shared/TagChip/CertificationChip";
import TagChip from "@/components/Shared/TagChip/TagChip";
import TagColorPicker from "@/components/Shared/TagColorPicker/TagColorPicker";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "../../../utils/toast";

/** One entry in an org taxonomy (tag type or certification type). */
export interface TaxonomyItem {
	key: string;
	name: { sv: string };
	validityDuration?: number;
	requiresProof?: boolean;
	hasExpirationDate?: boolean;
	visibleToConsultant?: boolean;
	color?: string;
	showInListViews?: boolean;
	remindBeforeExpiry?: boolean;
	reminderDaysBeforeExpiry?: number;
}

/** The editable subset of a taxonomy item (the key is never edited directly). */
export interface TaxonomyItemChanges {
	name: { sv: string };
	validityDuration?: number;
	requiresProof?: boolean;
	hasExpirationDate?: boolean;
	visibleToConsultant?: boolean;
	color?: string;
	showInListViews?: boolean;
	remindBeforeExpiry?: boolean;
	reminderDaysBeforeExpiry?: number;
}

interface TaxonomyCrudEditorProps {
	items: TaxonomyItem[];
	/** Label for the button revealing the create form, e.g. "Lägg till tagg". */
	addLabel: string;
	/** Shown when the taxonomy is empty. */
	emptyText: string;
	/** Show the optional validity-duration field on create/edit. */
	showValidity?: boolean;
	/** Unit for the validity duration — days (certifications) or hours (consultant tags). */
	validityUnit?: "days" | "hours";
	/** Show the requires-proof checkbox on create/edit (certifications). */
	showRequiresProof?: boolean;
	/** Show the "Har utgångsdatum" checkbox on create/edit (certifications). */
	showHasExpiration?: boolean;
	/** Show the "Är publik" (visibleToConsultant) checkbox on create/edit (certifications). */
	showVisibleToConsultant?: boolean;
	/** Show the tag color picker on create/edit (tag taxonomies). */
	showColor?: boolean;
	/**
	 * How the name label renders. "tag" (default) uses the colored {@link TagChip};
	 * "credential" uses a white bordered pill with an award icon, matching how an
	 * assigned certification is shown on the consultant competence tab.
	 */
	badgeVariant?: "tag" | "credential";
	/** Show the opt-in "Visa i listvyer" checkbox on create/edit. */
	showListViewVisibility?: boolean;
	/**
	 * Show the expiry-reminder toggle + day count on create/edit (certifications).
	 * Only offered together with "Har utgångsdatum" — a certification without an
	 * expiry date can never be reminded about.
	 */
	showReminder?: boolean;
	/** Disables all actions while a mutation is in flight. */
	isSaving?: boolean;
	onCreate: (changes: TaxonomyItemChanges) => Promise<unknown>;
	onUpdate: (key: string, changes: TaxonomyItemChanges) => Promise<unknown>;
	/**
	 * Delete the item. May reject with error code TAG_TYPE_IN_USE (consultant
	 * tags used in an active job's requirements) — surfaced in a blocked-dialog.
	 */
	onDelete: (key: string) => Promise<unknown>;
	deleteTitle: (item: TaxonomyItem) => string;
	/** Trello-style destructive copy, e.g. "Taggen tas bort från alla kunder. Detta går inte att ångra." */
	deleteDescription: (item: TaxonomyItem) => string;
}

/** Best-effort extraction of the fruster error body from a rejected API call. */
const getApiError = (err: unknown): { code?: string; detail?: string } => {
	const anyErr = err as any;
	if (anyErr?.error && typeof anyErr.error === "object") return anyErr.error;
	return anyErr ?? {};
};

/** Prefilled day count when expiry reminders are turned on for a type. */
const DEFAULT_REMINDER_DAYS = 10;

const parseDuration = (text: string): number | undefined => {
	const duration = parseInt(text, 10);
	return Number.isFinite(duration) && duration > 0 ? duration : undefined;
};

/** Submit/cancel button styles shared by the inline settings-leaf editors. */
export const primaryButtonClasses =
	"rounded-md bg-brand px-3.5 py-1.5 text-sm font-medium text-brand-foreground transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50";
export const secondaryButtonClasses =
	"rounded-md border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-50";

/** The draft being edited in the create/edit form. */
interface Draft {
	name: string;
	validity: string;
	requiresProof: boolean;
	hasExpirationDate: boolean;
	visibleToConsultant: boolean;
	color?: string;
	showInListViews: boolean;
	remindBeforeExpiry: boolean;
	reminderDays: string;
}

const emptyDraft: Draft = {
	name: "",
	validity: "",
	requiresProof: false,
	hasExpirationDate: false,
	visibleToConsultant: false,
	color: undefined,
	showInListViews: false,
	remindBeforeExpiry: false,
	reminderDays: "",
};

/**
 * Shared list + create/rename/delete editor for the org taxonomies
 * (consultant/customer/job tag types and certification types). Entries render
 * as Trello-style colored labels; the technical key is never shown.
 * Persistence is injected via `onCreate`/`onUpdate`/`onDelete` so the same UI
 * works both for the dedicated tag-type endpoints and for whole-array org PUT
 * fields.
 */
const TaxonomyCrudEditor = ({
	items,
	addLabel,
	emptyText,
	showValidity,
	validityUnit = "days",
	showRequiresProof,
	showHasExpiration,
	showVisibleToConsultant,
	showColor,
	showListViewVisibility,
	showReminder,
	badgeVariant = "tag",
	isSaving,
	onCreate,
	onUpdate,
	onDelete,
	deleteTitle,
	deleteDescription,
}: TaxonomyCrudEditorProps) => {
	// One shared draft: `editingKey === null` + `isCreating` → create form,
	// `editingKey` set → inline edit of that entry.
	const [isCreating, setIsCreating] = useState(false);
	const [editingKey, setEditingKey] = useState<string | null>(null);
	const [draft, setDraft] = useState<Draft>(emptyDraft);

	// Focus the name input whenever the create/edit form is revealed.
	const nameInputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (isCreating || editingKey) nameInputRef.current?.focus();
	}, [isCreating, editingKey]);

	// Delete confirm + "delete blocked" info dialog
	const [deleteTarget, setDeleteTarget] = useState<TaxonomyItem | null>(null);
	const [deleteBlocked, setDeleteBlocked] = useState<{ name: string; detail?: string } | null>(
		null
	);

	const validityUnitLabel = validityUnit === "hours" ? "timmar" : "dagar";

	const patchDraft = (patch: Partial<Draft>) => setDraft((prev) => ({ ...prev, ...patch }));

	const closeForm = () => {
		setIsCreating(false);
		setEditingKey(null);
		setDraft(emptyDraft);
	};

	const startCreate = () => {
		setEditingKey(null);
		setDraft(emptyDraft);
		setIsCreating(true);
	};

	const startEdit = (item: TaxonomyItem) => {
		setIsCreating(false);
		setEditingKey(item.key);
		setDraft({
			name: item.name.sv,
			validity: item.validityDuration != null ? String(item.validityDuration) : "",
			requiresProof: !!item.requiresProof,
			hasExpirationDate: !!item.hasExpirationDate,
			visibleToConsultant: !!item.visibleToConsultant,
			color: item.color,
			showInListViews: !!item.showInListViews,
			remindBeforeExpiry: !!item.remindBeforeExpiry,
			reminderDays:
				item.reminderDaysBeforeExpiry != null ? String(item.reminderDaysBeforeExpiry) : "",
		});
	};

	const buildChanges = (): TaxonomyItemChanges => ({
		name: { sv: draft.name.trim() },
		...(showValidity ? { validityDuration: parseDuration(draft.validity) } : {}),
		...(showRequiresProof ? { requiresProof: draft.requiresProof || undefined } : {}),
		...(showHasExpiration ? { hasExpirationDate: draft.hasExpirationDate || undefined } : {}),
		...(showVisibleToConsultant
			? { visibleToConsultant: draft.visibleToConsultant || undefined }
			: {}),
		...(showColor ? { color: draft.color || undefined } : {}),
		...(showListViewVisibility ? { showInListViews: draft.showInListViews || undefined } : {}),
		// Reminders hang off the expiry date — an entry without one is never reminded
		// about, so unticking "Har utgångsdatum" drops the reminder config with it.
		...(showReminder && draft.hasExpirationDate && draft.remindBeforeExpiry
			? {
					remindBeforeExpiry: true,
					reminderDaysBeforeExpiry: parseDuration(draft.reminderDays) ?? DEFAULT_REMINDER_DAYS,
			  }
			: {}),
	});

	const handleSubmit = async () => {
		if (!draft.name.trim()) return;
		if (editingKey) {
			await onUpdate(editingKey, buildChanges());
		} else {
			await onCreate(buildChanges());
		}
		closeForm();
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		const target = deleteTarget;
		try {
			await onDelete(target.key);
		} catch (err) {
			const { code, detail } = getApiError(err);
			if (code === "TAG_TYPE_IN_USE") {
				setDeleteBlocked({ name: target.name.sv, detail });
			} else {
				toast.errorGeneric();
			}
		}
	};

	// The name label — a colored tag chip, or (for certifications) a white
	// bordered award pill matching the consultant competence tab.
	const renderBadge = (name: string, color?: string) =>
		badgeVariant === "credential" ? (
			<CertificationChip name={name} />
		) : (
			<TagChip name={name} color={color} />
		);

	const renderMeta = (item: TaxonomyItem) => {
		const meta: string[] = [];
		if (showValidity && item.validityDuration != null)
			meta.push(`Giltig i ${item.validityDuration} ${validityUnitLabel}`);
		if (showRequiresProof && item.requiresProof) meta.push("Kräver underlag");
		if (showHasExpiration && item.hasExpirationDate) meta.push("Har utgångsdatum");
		if (showVisibleToConsultant && item.visibleToConsultant) meta.push("Publik");
		if (showListViewVisibility && item.showInListViews) meta.push("Visas i listvyer");
		if (showReminder && item.remindBeforeExpiry)
			meta.push(`Påminnelse ${item.reminderDaysBeforeExpiry ?? DEFAULT_REMINDER_DAYS} dagar innan`);
		if (!meta.length) return null;
		return <span className="text-xs text-gray-500">{meta.join(" · ")}</span>;
	};

	const renderViewRow = (item: TaxonomyItem) => (
		<div
			key={item.key}
			className="group flex items-center justify-between gap-3 border-t border-gray-200 py-2.5 first:border-t-0 first:pt-0"
		>
			<div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
				{renderBadge(item.name.sv, item.color)}
				{renderMeta(item)}
			</div>
			<div className="flex shrink-0 items-center gap-1">
				<button
					type="button"
					onClick={() => startEdit(item)}
					disabled={isSaving}
					aria-label="Redigera"
					title="Redigera"
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
				>
					<Pencil size={16} />
				</button>
				<button
					type="button"
					onClick={() => setDeleteTarget(item)}
					disabled={isSaving}
					aria-label="Ta bort"
					title="Ta bort"
					className="flex h-8 w-8 items-center justify-center rounded-sm border border-transparent text-gray-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
				>
					<Trash2 size={16} />
				</button>
			</div>
		</div>
	);

	const renderForm = (submitLabel: string) => (
		<div className="space-y-5 rounded-md border border-gray-200 bg-gray-50/50 p-3.5">
			<div className="flex flex-wrap items-end gap-2">
				<AppInputNew
					ref={nameInputRef}
					containerClassName="!w-auto flex-1 min-w-[180px]"
					label="Namn"
					skipLabelTranslation
					value={draft.name}
					onChangeText={(name: string) => patchDraft({ name })}
					onEnter={handleSubmit}
				/>
				{showValidity && (
					<AppInputNew
						containerClassName="!w-[170px]"
						label={`Giltighetstid (${validityUnitLabel})`}
						skipLabelTranslation
						showKnowledgeBaseForTagOnHover={validityUnit === "hours" ? "tag-validity" : undefined}
						type="number"
						min={1}
						value={draft.validity}
						onChangeText={(validity: string) => patchDraft({ validity })}
						placeholder="Ingen"
					/>
				)}
			</div>
			{showColor && (
				<TagColorPicker
					value={draft.color}
					onChange={(color) => patchDraft({ color })}
					disabled={isSaving}
				/>
			)}
			{showRequiresProof && (
				<CheckboxNew
					label="Kräver underlag (dokument)"
					skipTranslation
					checked={draft.requiresProof}
					onCheckedChange={(requiresProof) => patchDraft({ requiresProof })}
				/>
			)}
			{showHasExpiration && (
				<CheckboxNew
					label="Har utgångsdatum"
					hint="Ett utgångsdatum måste anges när certifikatet tilldelas en konsult. Lämna av om certifikatet aldrig går ut."
					skipTranslation
					checked={draft.hasExpirationDate}
					onCheckedChange={(hasExpirationDate) =>
						// Without an expiry date there is nothing to remind about
						patchDraft(
							hasExpirationDate
								? { hasExpirationDate }
								: { hasExpirationDate, remindBeforeExpiry: false }
						)
					}
				/>
			)}
			{showReminder && draft.hasExpirationDate && (
				<div className="space-y-3">
					<CheckboxNew
						label="Påminn konsulten innan certifikatet går ut"
						hint="Konsulten får en pushnotis i appen. Meddelandetexten redigeras under Notisinställningar."
						skipTranslation
						checked={draft.remindBeforeExpiry}
						onCheckedChange={(remindBeforeExpiry) =>
							patchDraft({
								remindBeforeExpiry,
								// Prefill the default the first time reminders are turned on
								reminderDays:
									remindBeforeExpiry && !draft.reminderDays
										? String(DEFAULT_REMINDER_DAYS)
										: draft.reminderDays,
							})
						}
					/>
					{draft.remindBeforeExpiry && (
						<AppInputNew
							containerClassName="!w-[210px]"
							label="Antal dagar innan"
							skipLabelTranslation
							type="number"
							min={1}
							value={draft.reminderDays}
							onChangeText={(reminderDays: string) => patchDraft({ reminderDays })}
							placeholder={String(DEFAULT_REMINDER_DAYS)}
						/>
					)}
				</div>
			)}
			{showVisibleToConsultant && (
				<CheckboxNew
					label="Publik"
					hint="Visas för konsulten, t.ex. i konsultappen."
					skipTranslation
					checked={draft.visibleToConsultant}
					onCheckedChange={(visibleToConsultant) => patchDraft({ visibleToConsultant })}
				/>
			)}
			{showListViewVisibility && (
				<CheckboxNew
					label="Visa i listvyer"
					hint="Visas direkt på raden i listvyer för bättre överblick."
					skipTranslation
					checked={draft.showInListViews}
					onCheckedChange={(showInListViews) => patchDraft({ showInListViews })}
				/>
			)}
			{draft.name.trim() && (
				<div className="flex items-center gap-2 text-xs text-gray-500">
					<span>Förhandsvisning:</span>
					{renderBadge(draft.name.trim(), draft.color)}
				</div>
			)}
			<div className="flex items-center gap-2 border-t pt-4">
				<button
					type="button"
					onClick={handleSubmit}
					disabled={!draft.name.trim() || isSaving}
					className={primaryButtonClasses}
				>
					{submitLabel}
				</button>
				<button
					type="button"
					onClick={closeForm}
					disabled={isSaving}
					className={secondaryButtonClasses}
				>
					Avbryt
				</button>
			</div>
		</div>
	);

	return (
		<>
			{items.length === 0 ? (
				<p className="text-sm text-gray-500">{emptyText}</p>
			) : (
				<div>
					{items.map((item) =>
						item.key === editingKey ? (
							<div
								key={item.key}
								className="border-t border-gray-200 py-2.5 first:border-t-0 first:pt-0"
							>
								{renderForm("Spara")}
							</div>
						) : (
							renderViewRow(item)
						)
					)}
				</div>
			)}

			<div className="border-t border-gray-200 pt-4">
				{isCreating ? (
					renderForm("Lägg till")
				) : (
					<AddButton label={addLabel} onClick={startCreate} disabled={isSaving} />
				)}
			</div>

			<AppAlertDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				variant="danger"
				skipTranslation
				title={deleteTarget ? deleteTitle(deleteTarget) : ""}
				description={deleteTarget ? deleteDescription(deleteTarget) : ""}
				confirmLabel="Ta bort"
				cancelLabel="Avbryt"
				onConfirm={handleConfirmDelete}
			/>

			<AlertDialog
				open={!!deleteBlocked}
				onOpenChange={(open) => {
					if (!open) setDeleteBlocked(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Taggen kan inte tas bort</AlertDialogTitle>
						<AlertDialogDescription>
							{`"${deleteBlocked?.name}" används som krav i ett eller flera aktiva uppdrag och kan därför inte tas bort. Ta bort kravet från uppdragen och försök igen.`}
						</AlertDialogDescription>
					</AlertDialogHeader>
					{deleteBlocked?.detail && <p className="text-xs text-gray-500">{deleteBlocked.detail}</p>}
					<AlertDialogFooter>
						<AlertDialogAction onClick={() => setDeleteBlocked(null)}>OK</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

export default TaxonomyCrudEditor;
